export type RouteLocation = string | google.maps.LatLng | google.maps.LatLngLiteral;

export interface DrivingRouteRequest {
    origin: RouteLocation;
    destination: RouteLocation;
    intermediates?: google.maps.routes.Waypoint[];
    computeAlternativeRoutes?: boolean;
}

const ROUTE_FIELDS = [
    'path',
    'legs',
    'distanceMeters',
    'durationMillis',
    'localizedValues',
    'viewport'
];

export async function computeDrivingRoutes(request: DrivingRouteRequest): Promise<google.maps.routes.Route[]> {
    const { Route } = await google.maps.importLibrary('routes') as google.maps.RoutesLibrary;
    const result = await Route.computeRoutes({
        origin: request.origin,
        destination: request.destination,
        intermediates: request.intermediates,
        computeAlternativeRoutes: request.computeAlternativeRoutes && !request.intermediates?.length,
        travelMode: 'DRIVING',
        fields: ROUTE_FIELDS
    });
    return result.routes || [];
}

export function renderRoutePolylines(
    route: google.maps.routes.Route,
    map: google.maps.Map,
    options: google.maps.PolylineOptions
): google.maps.Polyline[] {
    const polylines = route.createPolylines({ polylineOptions: options });
    for (let index = 0; index < polylines.length; index++)
        polylines[index].setMap(map);
    return polylines;
}

export function clearRoutePolylines(polylines: google.maps.Polyline[]): void {
    for (let index = 0; index < polylines.length; index++)
        polylines[index].setMap(null);
}

export function getRouteMetrics(route: google.maps.routes.Route): { distance: string; duration: string } {
    const distance = route.localizedValues?.distance || formatDistance(route.distanceMeters);
    const duration = route.localizedValues?.duration || formatDuration(route.durationMillis);
    return { distance, duration };
}

function formatDistance(meters?: number): string {
    if (!Number.isFinite(meters))
        return 'N/A';
    if (meters! < 1000)
        return `${Math.round(meters!)} m`;
    return `${(meters! / 1000).toFixed(1)} km`;
}

function formatDuration(milliseconds?: number | null): string {
    if (!Number.isFinite(milliseconds))
        return 'N/A';
    const minutes = Math.max(1, Math.round(milliseconds! / 60_000));
    if (minutes < 60)
        return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainder = minutes % 60;
    return remainder ? `${hours} hr ${remainder} min` : `${hours} hr`;
}
